import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Handles the `#session_id=...` fragment returned by Emergent OAuth.
 * Exchanges once (StrictMode-safe via useRef) and routes the user to the
 * destination they intended (or /dashboard by default).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);

    (async () => {
      try {
        await apiClient.post(
          "/auth/session",
          { session_id },
          { withCredentials: true },
        );
        await refresh();
        // Clean the hash + route to intended destination
        const dest =
          (location.pathname && location.pathname !== "/auth/callback"
            ? location.pathname
            : null) ||
          new URLSearchParams(location.search).get("next") ||
          "/dashboard";
        navigate(dest, { replace: true });
      } catch (e) {
        navigate("/?auth_error=1", { replace: true });
      }
    })();
  }, [navigate, location, refresh]);

  return (
    <section
      className="min-h-[60vh] bg-[#0A0A0A] text-white grid place-items-center"
      data-testid="auth-callback"
    >
      <div className="flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-[#0047FF]" />
        <span className="font-mono text-xs uppercase tracking-widest text-white/70">
          Signing you in…
        </span>
      </div>
    </section>
  );
}
